import { defineStore } from "pinia";
import { constants } from "@/constants";

// Holds the customer's coregistration opt-in selection for the current order page.
// Masked coregs are registered as always-checked; visible coregs reflect the
// checkbox state. `selectedSourceCodes` feeds the Curiosity subscription.
//
// Selection is mirrored to sessionStorage so it survives the full-page redirect
// from checkout to /waiting (where Pinia is recreated and Curiosity runs).

function readPersistedSelection() {
  try {
    const raw = sessionStorage.getItem(constants.ORDER_PAGE_COREG_SELECTION);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch (_) {
    return {};
  }
}

function persistSelection(selection) {
  try {
    sessionStorage.setItem(
      constants.ORDER_PAGE_COREG_SELECTION,
      JSON.stringify(selection),
    );
  } catch (_) {
    /* sessionStorage unavailable (privacy mode, etc.) */
  }
}

export const useCoregStore = defineStore("coreg", {
  state: () => ({
    _selection: readPersistedSelection(),
  }),
  getters: {
    selectedSourceCodes: (state) =>
      Object.keys(state._selection).filter((code) => state._selection[code]),
  },
  actions: {
    // Default from config: do not overwrite a value already set (persisted or user).
    register(trackingCode, checked) {
      if (!trackingCode) {
        return;
      }
      if (Object.prototype.hasOwnProperty.call(this._selection, trackingCode)) {
        return;
      }
      this._selection = { ...this._selection, [trackingCode]: !!checked };
      persistSelection(this._selection);
    },
    // Explicit user toggle: always overwrite.
    setChecked(trackingCode, checked) {
      if (!trackingCode) {
        return;
      }
      this._selection = { ...this._selection, [trackingCode]: !!checked };
      persistSelection(this._selection);
    },
    reset() {
      this._selection = {};
      persistSelection(this._selection);
    },
  },
});
